import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import { HowItWorks, SupportedContracts, WhyThisExists, Pricing, FAQ, Terms, Privacy, Disclaimer, Contact } from "@/pages/PublicPages";
import { Login, Register, Forgot, Reset } from "@/pages/Auth";
import { Dashboard, Profile, Notifications, ContractLibrary, Billing, BillingSuccess, BillingCancel } from "@/pages/EmployeePages";
import { GrievanceBuilder, CaseDetail } from "@/pages/GrievanceBuilder";
import { IncidentsList, IncidentDetail } from "@/pages/Incidents";
import { StewardDashboard, StewardMeetings, AdminDashboard, AdminUsers, AdminCases, AdminMessages, AdminArticles, AdminCategories } from "@/pages/StewardAdmin";
import { AdminContractLibrary, AdminContractReview, AdminContractViewer, AdminContractSearch, AdminContractTaxonomy } from "@/pages/AdminContractLibrary";
import { WizardErrorBoundary } from "@/components/WizardErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ReferralCapture } from "@/components/ReferralCapture";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ReferralCapture />
          <Toaster position="bottom-right" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/supported-contracts" element={<SupportedContracts />} />
            <Route path="/why" element={<WhyThisExists />} />
            <Route path="/why-it-exists" element={<WhyThisExists />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />

            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/contract-library" element={<Protected><ContractLibrary /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/billing/cancel" element={<Protected><BillingCancel /></Protected>} />
            <Route path="/new-grievance" element={<Protected><WizardErrorBoundary><GrievanceBuilder /></WizardErrorBoundary></Protected>} />
            <Route path="/new-grievance/:id" element={<Protected><WizardErrorBoundary><GrievanceBuilder /></WizardErrorBoundary></Protected>} />
            <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
            <Route path="/incidents" element={<Protected><IncidentsList /></Protected>} />
            <Route path="/incidents/:id" element={<Protected><IncidentDetail /></Protected>} />

            <Route path="/steward-dashboard" element={<Protected roles={["steward","admin"]}><StewardDashboard /></Protected>} />
            <Route path="/steward-meetings" element={<Protected roles={["steward","admin"]}><StewardMeetings /></Protected>} />

            <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
            <Route path="/admin/cases" element={<Protected roles={["admin"]}><AdminCases /></Protected>} />
            <Route path="/admin/messages" element={<Protected roles={["admin"]}><AdminMessages /></Protected>} />
            <Route path="/admin/articles" element={<Protected roles={["admin"]}><AdminArticles /></Protected>} />
            <Route path="/admin/categories" element={<Protected roles={["admin"]}><AdminCategories /></Protected>} />

            {/* Phase 1A: Contract Knowledge Base (admin-only, isolated) */}
            <Route path="/admin/contracts" element={<Protected roles={["admin"]}><AdminContractLibrary /></Protected>} />
            <Route path="/admin/contracts/taxonomy" element={<Protected roles={["admin"]}><AdminContractTaxonomy /></Protected>} />
            <Route path="/admin/contracts/viewer" element={<Protected roles={["admin"]}><AdminContractViewer /></Protected>} />
            <Route path="/admin/contracts/search" element={<Protected roles={["admin"]}><AdminContractSearch /></Protected>} />
            <Route path="/admin/contracts/:aid/review" element={<Protected roles={["admin"]}><AdminContractReview /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
